import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { register } from '../services/authService';

// Public self-service registration page.
//
// Submits to POST /register which (since v2) creates an account_invitation row
// rather than a User row directly. The user must click the verification link
// in the email (or printed to the backend log in development) to activate
// their account.
//
// The user_type selector is currently limited to "owner" — the only public
// self-signup type. Agents are created via the invitation flow initiated by
// agency staff; renters are created via a future invitation endpoint; system
// users are created by an admin endpoint. Add new options here when those
// flows become available.
const USER_TYPE_OPTIONS = [
  { value: 'owner', label: 'Property Owner' },
];

const Register = () => {
  const [form, setForm] = useState({
    given: '',
    surname: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    userType: 'owner',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingEmail, setPendingEmail] = useState(null);

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      setLoading(false);
      return;
    }

    try {
      const response = await register({
        given: form.given,
        surname: form.surname,
        email: form.email,
        password: form.password,
        mobile: form.mobile || undefined,
        user_type: form.userType,
      });
      setPendingEmail(response.email || form.email);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ---- Success screen ------------------------------------------------------
  if (pendingEmail) {
    return (
      <div className="min-h-screen flex items-center bg-gray-50">
        <div className="max-w-md w-full mx-auto px-4">
          <div className="bg-white shadow-2xl border-0 rounded-xl overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white text-center py-6">
              <h3 className="text-2xl font-bold mb-0">Check your email</h3>
              <p className="text-emerald-100 mb-0">One more step to activate your account</p>
            </div>
            <div className="p-6 space-y-4 text-gray-700">
              <p>
                We've sent a verification link to{' '}
                <span className="font-semibold">{pendingEmail}</span>.
              </p>
              <p className="text-sm text-gray-600">
                Click the link in that email to activate your account. The link will
                expire in 48 hours. If you don't see the email, check your spam folder.
              </p>
              <p className="text-sm text-gray-500">
                Already verified?{' '}
                <Link to="/login" className="text-emerald-600 hover:text-emerald-800 font-medium">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Form ----------------------------------------------------------------
  return (
    <div className="min-h-screen flex items-center bg-gray-50">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="bg-white shadow-2xl border-0 rounded-xl overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white text-center py-6">
            <h3 className="text-2xl font-bold mb-0">Create Account</h3>
            <p className="text-emerald-100 mb-0">Join Rental Management System</p>
          </div>
          <div className="p-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="given" className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                  <input
                    type="text"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                    id="given"
                    name="given"
                    value={form.given}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter first name"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label htmlFor="surname" className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                  <input
                    type="text"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                    id="surname"
                    name="surname"
                    value={form.surname}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter last name"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                  id="email"
                  name="email"
                  value={form.email}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your email"
                  disabled={loading}
                />
              </div>

              <div className="mb-4">
                <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-2">Mobile (optional)</label>
                <input
                  type="tel"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                  id="mobile"
                  name="mobile"
                  value={form.mobile}
                  onChange={handleInputChange}
                  placeholder="+61 412 345 678"
                  disabled={loading}
                />
              </div>

              <div className="mb-4">
                <label htmlFor="userType" className="block text-sm font-medium text-gray-700 mb-2">Account Type</label>
                <select
                  id="userType"
                  name="userType"
                  value={form.userType}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                  disabled={loading}
                >
                  {USER_TYPE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="mt-1.5 text-xs text-gray-500">
                  Agents and renters are added via invitation from an agency.
                </p>
              </div>

              <div className="mb-4">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                  id="password"
                  name="password"
                  value={form.password}
                  onChange={handleInputChange}
                  required
                  placeholder="Minimum 8 characters"
                  disabled={loading}
                />
              </div>

              <div className="mb-6">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
                <input
                  type="password"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleInputChange}
                  required
                  placeholder="Confirm password"
                  disabled={loading}
                />
              </div>

              <div className="w-full">
                <button
                  type="submit"
                  className="w-full px-4 py-3 text-lg font-medium text-white bg-emerald-600 border border-transparent rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending verification email…
                    </>
                  ) : (
                    'Create Account'
                  )}
                </button>
              </div>
            </form>
          </div>
          <div className="text-center py-4 bg-gray-50 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-emerald-600 hover:text-emerald-800 font-medium transition-colors duration-200">
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;

